import React, { useContext, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues, useForm } from 'react-hook-form';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import useTranslation from '../../../../../Hooks/useTranslation';
import {
	FieldNames,
	HealthPlanContext,
} from '../../../../../Services/HealthBabyPlanDataContext';
import { useCreateHealthPlanUserFromDataMutation } from '../../../../../graphql/graphql';
import { useHealthPlanContext } from '../../../../../Services/HealthPlanContext';
import ErrorMessage from '../../../../../Components/Form/components/ErrorMessage';
import { joinClasses } from '../../../../../Utils/className.util';
import Loading from '../../../../../Components/Loading';
import ScrollContainer from '../../../../Base/Components/ScrollContainer';
import FinalStepFields, {
	FINAL_STEP_VALIDATION_TRANSLATION_PREFIX,
	getFinalStepValidationSchema,
} from '../FieldsForms/FinalStepFields';
import { useAppContext } from '../../../../../Services/AppContext';
// @ts-expect-error
import styles from './FinalStepForm.module.scss';
import { getDateDifference, getDateDifferenceMoment } from '../utils/date';
import SavingSpinner from '../../../../../Components/Base/Loading/SavingSpinner';
import useFormDraft from '../../../../../Components/Form/FormDraftState/useFormDraft';
import { FormDraftStateKey } from '../../../../../Components/Form/FormDraftState/FormDraftStateKey';
import { removeKeys } from '../../../../../Services/LocalStorage';
import { getMomentDate } from '../../../../../Utils/dates.util';
import FormControl from '../../../../../Components/Form/FormControl';
import Checkbox from '../../../../../Components/Base/Checkbox/Checkbox';
import PatientNoConsentProvided from '../../../../Patient/Components/PatientNoConsentProvided/PatientNoConsentProvided';
import usePatientConsent from '../../../../../Components/PatientProfile/Hooks/usePatientConsent';
import useIdentificationCode from '../hooks/useIdentificationCode';

interface Props {
	patientId: string;
	workplaceId: string;
	onHealthPlanCreate: () => void;
}

const ERROR_NAMES = {
	HealthPlanUserExistsError: `${FINAL_STEP_VALIDATION_TRANSLATION_PREFIX}.healthPlanUserExistsError`,
};

const FinalStepForm: React.FC<Props> = ({
	patientId,
	workplaceId,
	onHealthPlanCreate,
}) => {
	const { t } = useTranslation();
	const { data, setData } = useContext(HealthPlanContext);
	const { isConsentProvided } = usePatientConsent(patientId);
	const {
		userData: { id: loggedUserId },
	} = useAppContext();
	const { healthPlan, refetchHealthPlanUser } = useHealthPlanContext();
	const { getNextIdentificationCode, updateLastIdentificationCode } =
		useIdentificationCode();

	const [createHealthPlanUserFromData, { isLoading, isError, error }] =
		useCreateHealthPlanUserFromDataMutation();

	const defaultValues = {
		finalStep: {
			identificationCode: getNextIdentificationCode(),
		},
	};

	const formMethods = useForm({
		defaultValues,
		resolver: yupResolver(getFinalStepValidationSchema(t, true)),
	});

	const { formDraftIsSaving } = useFormDraft({
		formMethods,
		key: `${FormDraftStateKey.MEDDI_BABY_CREATE_FINAL_STEP}_${patientId}`,
		defaultValues,
	});

	const fieldValues = formMethods.watch(FieldNames.FINAL_STEP);

	const { weeks, days } = getDateDifferenceMoment({
		estimatedDateOfBirth: getMomentDate(
			data?.[FieldNames.CURRENT_PREGNANCY]?.plannedDateOfBirth,
		),
		dateOfBirth: getMomentDate(fieldValues?.careDate),
	});

	const handleSubmitForm = async (fields: FieldValues) => {
		const ingestion = [
			{
				key: 'weight',
				value: data?.mothersHealth?.actualWeight,
			},
			{
				key: 'blood_pressure_systolic',
				value: data?.mothersHealth?.systolicBloodPressure,
			},
			{
				key: 'blood_pressure_diastolic',
				value: data?.mothersHealth?.diastolicBloodPressure,
			},
		];

		try {
			const response = await createHealthPlanUserFromData({
				input: {
					userId: patientId,
					healthPlanId: healthPlan.id,
					questionnaire: {
						...data,
						...fields,
					},
					ingestion: ingestion,
					addToPatientECard: fields?.addToPatientECard ?? false,
				},
			});

			// @ts-expect-error
			if (response.data) {
				refetchHealthPlanUser();
				setData(fields);
				removeKeys([
					`form_draft_${FormDraftStateKey.MEDDI_BABY_CREATE_CURRENT_PREGNANCY}_${patientId}`,
					`form_draft_${FormDraftStateKey.MEDDI_BABY_CREATE_PREVIOUS_PREGNANCY}_${patientId}`,
					`form_draft_${FormDraftStateKey.MEDDI_BABY_CREATE_MOTHERS_HEALTH}_${patientId}`,
					`form_draft_${FormDraftStateKey.MEDDI_BABY_CREATE_LAB_RESULTS}_${patientId}`,
					`form_draft_${FormDraftStateKey.MEDDI_BABY_CREATE_FINAL_STEP}_${patientId}`,
				]);
				await updateLastIdentificationCode();
				onHealthPlanCreate();
			}
		} catch {}
	};

	return (
		<ScrollContainer>
			<div className='col-10'>
				<FormWrapper formMethods={formMethods}>
					{isLoading && <Loading absolute />}
					<div
						className={joinClasses([
							styles.form,
							isLoading ? styles.disabledForm : '',
						])}>
						<FinalStepFields
							isEntryQuestionnaire
							patientId={patientId}
							workplaceId={workplaceId}
							dateDifference={weeks && days ? `${weeks} + ${days}` : ''}
						/>
						{isError && (
							<div className={styles.errorMessage}>
								<ErrorMessage
									tx={
										// @ts-expect-error
										ERROR_NAMES[error?.errors[0].name] ??
										`${FINAL_STEP_VALIDATION_TRANSLATION_PREFIX}.createHealthPlanFailed`
									}
								/>
							</div>
						)}
						{isConsentProvided ? (
							<div className={'mt-4'}>
								<FormControl name='addToPatientECard' hideRequired={true}>
									<Checkbox tx={'base.addToPatientECard'} />
								</FormControl>
							</div>
						) : (
							<PatientNoConsentProvided patientId={patientId} />
						)}
						<div className='d-flex mt-4 justify-content-between'>
							<SubmitButton
								title='base.shared.done'
								disabled={isLoading}
								onSubmit={handleSubmitForm}
							/>
							{formDraftIsSaving && <SavingSpinner />}
						</div>
					</div>
				</FormWrapper>
			</div>
		</ScrollContainer>
	);
};

export default FinalStepForm;
